import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  CartItem,
  Payment,
  PaymentType,
  Product,
  Transaction,
  TransactionPayment,
  TransactionProduct,
} from '@/lib/types';
import { toEuros } from '@/lib/price-formatter';
import { useConfigStore } from '@/store/config-store';
import { useAuthStore } from '@/store/auth-store';
import { useCashStore } from '@/store/cash-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CartState {
  items: CartItem[];
  payments: Payment[];
  tableId: string | null;
  reservationId: string | null;

  // --- Product actions ---
  addProduct: (product: Product, qty?: number) => void;
  removeProduct: (key: string) => void;
  updateQuantity: (key: string, qty: number) => void;

  // --- Payment actions ---
  addPayment: (type: PaymentType, amount: number) => void;
  removePayment: (index: number) => void;
  clearPayments: () => void;

  // --- General ---
  clear: () => void;
  setTable: (id: string | null) => void;
  setReservation: (id: string | null) => void;

  // --- Computed getters ---
  getTotal: () => number;
  getPaymentsTotal: () => number;
  getRemainingAmount: () => number;
  isFullyPaid: () => boolean;
  isValid: () => boolean;
  buildTransaction: () => Transaction;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULTS = {
  items: [] as CartItem[],
  payments: [] as Payment[],
  tableId: null as string | null,
  reservationId: null as string | null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      // ==================================================================
      // Product actions
      // ==================================================================

      addProduct: (product, qty = 1) => {
        const priceInEuros = toEuros(product.price);

        set((state) => {
          const existing = state.items.find((item) => item.key === product.key);

          if (existing) {
            return {
              items: state.items.map((item) =>
                item.key === product.key
                  ? {
                      ...item,
                      quantity: item.quantity + qty,
                      total: parseFloat(
                        ((item.quantity + qty) * item.price).toFixed(2),
                      ),
                    }
                  : item,
              ),
            };
          }

          const newItem: CartItem = {
            key: product.key,
            name: product.name,
            price: priceInEuros,
            quantity: qty,
            total: parseFloat((priceInEuros * qty).toFixed(2)),
            has_sequence: product.has_sequence || false,
          };

          return { items: [...state.items, newItem] };
        });
      },

      removeProduct: (key) =>
        set((state) => ({
          items: state.items.filter((item) => item.key !== key),
        })),

      updateQuantity: (key, qty) => {
        if (qty <= 0) {
          get().removeProduct(key);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.key === key
              ? {
                  ...item,
                  quantity: qty,
                  total: parseFloat((qty * item.price).toFixed(2)),
                }
              : item,
          ),
        }));
      },

      // ==================================================================
      // Payment actions
      // ==================================================================

      addPayment: (type, amount) => {
        if (amount <= 0) return;

        const remaining = get().getRemainingAmount();
        // Allow a tiny tolerance for floating point
        if (amount > remaining + 0.01) return;

        const payment: Payment = {
          type,
          amount: parseFloat(amount.toFixed(2)),
          timestamp: new Date().toISOString(),
        };

        set((state) => ({
          payments: [...state.payments, payment],
        }));
      },

      removePayment: (index) =>
        set((state) => ({
          payments: state.payments.filter((_, i) => i !== index),
        })),

      clearPayments: () => set({ payments: [] }),

      // ==================================================================
      // General
      // ==================================================================

      clear: () => set({ ...DEFAULTS }),

      setTable: (id) => set({ tableId: id }),

      setReservation: (id) => set({ reservationId: id }),

      // ==================================================================
      // Computed getters
      // ==================================================================

      getTotal: () => {
        const { items } = get();
        return parseFloat(
          items.reduce((sum, item) => sum + item.total, 0).toFixed(2),
        );
      },

      getPaymentsTotal: () => {
        const { payments } = get();
        return parseFloat(
          payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2),
        );
      },

      getRemainingAmount: () => {
        return Math.max(0, get().getTotal() - get().getPaymentsTotal());
      },

      isFullyPaid: () => {
        return get().getRemainingAmount() < 0.01;
      },

      isValid: () => {
        const { items } = get();
        return items.length > 0 && get().isFullyPaid();
      },

      /**
       * Build the full NF525-compliant transaction payload.
       * Reads cross-store data from configStore, authStore and cashStore
       * via their getState() — this is the Zustand best practice
       * for reading other stores inside actions.
       */
      buildTransaction: () => {
        const { items, payments, tableId, reservationId } = get();

        const configState = useConfigStore.getState();
        const authState = useAuthStore.getState();
        const cashState = useCashStore.getState();

        const transactionProducts: TransactionProduct[] = items.map(
          (item) => ({
            key: item.key,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            total: item.total,
          }),
        );

        const transactionPayments: TransactionPayment[] = payments.map(
          (p) => ({
            type: p.type,
            amount: p.amount,
          }),
        );

        const transaction: Transaction = {
          products: transactionProducts,
          payments: transactionPayments,
          table_id: tableId ?? undefined,
          reservation_id: reservationId ?? undefined,
          pos_id: configState.posId,
          employee_id: authState.user?.id ?? 0,
          cash_session_id: cashState.cashSessionId ?? 0,
          transaction_id: crypto.randomUUID(),
          client_created_at: new Date().toISOString(),
          device_id: configState.deviceId,
          source: 'caisse',
        };

        return transaction;
      },
    }),
    {
      name: 'sideview-caisse-cart',
      partialize: (state) => ({
        items: state.items,
        payments: state.payments,
        tableId: state.tableId,
        reservationId: state.reservationId,
      }),
    },
  ),
);
